<template>
  <div class="quotes-page">
    <!-- ==================== SKELETON LOADING STATE ==================== -->
    <template v-if="loading">
      <div class="skeleton-container">
        <!-- Skeleton Header -->
        <div class="skeleton-header-section">
          <div class="skeleton-header-content">
            <div class="skeleton-header-text">
              <div class="skeleton-title"></div>
              <div class="skeleton-subtitle"></div>
            </div>
            <div class="skeleton-stats-card">
              <div class="skeleton-stats-icon"></div>
              <div class="skeleton-stats-info">
                <div class="skeleton-stats-number"></div>
                <div class="skeleton-stats-label"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton Filters -->
        <div class="skeleton-filters">
          <div class="skeleton-filter-btn"></div>
          <div class="skeleton-filter-btn"></div>
          <div class="skeleton-filter-btn"></div>
        </div>

        <!-- Skeleton Quotes Grid -->
        <div class="skeleton-quotes-grid">
          <div
            v-for="n in 6"
            :key="'skeleton-card-' + n"
            class="skeleton-quote-card"
          >
            <div class="skeleton-card-header">
              <div class="skeleton-quote-id"></div>
              <div class="skeleton-status-badge"></div>
            </div>
            <div class="skeleton-card-body">
              <div class="skeleton-property-name"></div>
              <div class="skeleton-project-name"></div>
              <div class="skeleton-details">
                <div class="skeleton-detail-item"></div>
                <div class="skeleton-detail-item"></div>
              </div>
            </div>
            <div class="skeleton-card-footer">
              <div class="skeleton-action-btn"></div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- ==================== MAIN CONTENT ==================== -->
    <template v-else>
      <!-- Header Section with Stats -->
      <div class="quotes-header-section" ref="mainContent">
        <div class="quotes-header-content">
          <div class="quotes-header-text">
            <h2 class="quotes-title">
              Mis {{ customer == "clab" ? "Simulaciones" : quoteText }}
            </h2>
            <p class="quotes-subtitle">
              Sigue el estado real de cada una de las
              {{ customer == "clab" ? "simulaciones" : quoteText }} que has
              realizado en nuestros proyectos.
            </p>
          </div>
          <div class="quotes-stats-card" v-if="items.length > 0">
            <div class="stats-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
                <path
                  d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>
            </div>
            <div class="stats-info">
              <span class="stats-number">{{ items.length }}</span>
              <span class="stats-label">
                {{ items.length === 1 ? quoteTextSingular : quoteText }}
                {{ items.length === 1 ? "registrada" : "registradas" }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Tabs -->
      <div class="quotes-filters" v-if="items.length > 0">
        <button
          class="filter-btn"
          :class="{ active: activeFilter === 'all' }"
          @click="activeFilter = 'all'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 6H20M4 12H20M4 18H20"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          Todas
          <span class="filter-count">{{ items.length }}</span>
        </button>
        <button
          class="filter-btn"
          :class="{ active: activeFilter === 'active' }"
          @click="activeFilter = 'active'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M9 12L11 14L15 10"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.5"
            />
          </svg>
          Vigentes
          <span class="filter-count">{{ activeQuotesCount }}</span>
        </button>
        <button
          class="filter-btn"
          :class="{ active: activeFilter === 'reserved' }"
          @click="activeFilter = 'reserved'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M19 21L12 16L5 21V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V21Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ country == 0 ? "Separadas" : "Reservadas" }}
          <span class="filter-count">{{ reservedQuotesCount }}</span>
        </button>
        <button
          class="filter-btn"
          :class="{ active: activeFilter === 'expired' }"
          @click="activeFilter = 'expired'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M15 9L9 15M9 9L15 15"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
          </svg>
          Vencidas
          <span class="filter-count">{{ expiredQuotesCount }}</span>
        </button>
      </div>

      <!-- Quotes Content Container -->
      <div class="quotes-content-container">
        <!-- Empty State -->
        <div v-if="items.length === 0" class="empty-state">
          <div class="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5C15 6.10457 14.1046 7 13 7H11C9.89543 7 9 6.10457 9 5Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
          </div>
          <h3 class="empty-title">Sin {{ quoteText }} aún</h3>
          <p class="empty-text">
            Comienza a cotizar propiedades para ver tu historial aquí.
          </p>
          <router-link
            v-if="showQuote"
            :to="{ name: 'quote' }"
            class="empty-action"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Nueva {{ quoteTextSingular }}
          </router-link>
        </div>

        <!-- No Results for Filter -->
        <div
          v-else-if="filteredItems.length === 0"
          class="empty-state filter-empty"
        >
          <div class="empty-icon small">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </div>
          <h3 class="empty-title">No hay {{ quoteText }} en esta categoría</h3>
          <p class="empty-text">
            Prueba seleccionando otro filtro para ver más resultados.
          </p>
          <button class="reset-filter-btn" @click="activeFilter = 'all'">
            Ver todas las {{ quoteText }}
          </button>
        </div>

        <!-- Quotes Grid -->
        <div v-else class="quotes-grid">
          <div
            v-for="(item, index) in paginatedItems"
            :key="index"
            class="quote-card"
            :class="getCardClass(item)"
          >
            <!-- Status Indicator Bar -->
            <div class="status-bar" :class="getStatusClass(item)"></div>

            <!-- Card Content -->
            <div class="card-content">
              <!-- Top Row: ID + Status Badge -->
              <div class="card-top">
                <div class="quote-meta">
                  <span class="quote-id">#{{ item.quote | numberFormat }}</span>
                  <span class="quote-separator">•</span>
                  <span class="quote-date">{{
                    formatDate(item.date, "DD MMM YYYY")
                  }}</span>
                </div>
                <div class="status-badge" :class="getStatusClass(item)">
                  {{ getStatusText(item) }}
                </div>
              </div>

              <!-- Main Info -->
              <div class="card-main">
                <h3 class="property-title">{{ item.asset }}</h3>
                <p class="project-title">{{ item.project }}</p>
              </div>

              <!-- Location -->
              <div class="card-location">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                  <path
                    d="M12 22C16 18 20 14.4183 20 10C20 5.58172 16.4183 2 12 2C7.58172 2 4 5.58172 4 10C4 14.4183 8 18 12 22Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>
                <span>{{ item.address }}</span>
              </div>

              <!-- Bottom Row: Validity + Action -->
              <div class="card-bottom">
                <!-- Validity Info -->
                <div class="validity-info" v-if="!item.reserve.isBusiness">
                  <template v-if="item.reserve.isReserve">
                    <span class="validity-tag active">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M12 8V12L15 15"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="currentColor"
                          stroke-width="1.5"
                        />
                      </svg>
                      {{ getDaysRemaining(item) }} días
                    </span>
                  </template>
                  <template v-else>
                    <span class="validity-tag expired">Expirada</span>
                  </template>
                </div>
                <div class="validity-info" v-else>
                  <span class="validity-tag reserved">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 12L11 14L15 10"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    {{ reservationText }}
                  </span>
                </div>

                <!-- Action -->
                <div class="card-action">
                  <template v-if="item.reserve.isBusiness">
                    <span class="action-completed">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M9 12L11 14L15 10"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="9"
                          stroke="currentColor"
                          stroke-width="1.5"
                        />
                      </svg>
                    </span>
                  </template>
                  <template v-else-if="item.reserve.isReserve">
                    <span v-if="country == 0" class="action-contact">
                      Contactar
                    </span>
                    <a
                      v-else
                      :href="item.reserve.link"
                      target="_blank"
                      class="action-btn"
                    >
                      Reservar
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M5 12H19M19 12L12 5M19 12L12 19"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </a>
                  </template>
                  <template v-else>
                    <span class="action-expired">—</span>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="filteredItems.length > perPage" class="pagination-wrapper">
          <div class="pagination-info">
            Mostrando {{ paginationStart }} - {{ paginationEnd }} de
            {{ filteredRows }} {{ quoteText }}
          </div>
          <b-pagination
            v-model="currentPage"
            :total-rows="filteredRows"
            :per-page="perPage"
            aria-controls="quote-table"
            class="custom-pagination"
            first-number
            last-number
            hide-goto-end-buttons
          >
            <template #prev-text>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
            <template #next-text>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 18L15 12L9 6"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </template>
          </b-pagination>
        </div>

        <!-- Info Note -->
        <div class="quotes-note" v-if="items.length > 0">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="1.5"
            />
            <path
              d="M12 8V12"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
            />
            <circle cx="12" cy="16" r="1" fill="currentColor" />
          </svg>
          <span>Se muestran las últimas 30 {{ quoteText }} realizadas.</span>
        </div>
      </div>

      <!-- CTA Banner -->
      <div class="cta-banner" v-if="showQuote && items.length > 0">
        <div class="cta-content">
          <div class="cta-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 5V19M5 12H19"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
          <div class="cta-text">
            <h4>¿Interesado en más propiedades?</h4>
            <p>
              Explora nuestro catálogo y realiza una nueva
              {{ quoteTextSingular }}
            </p>
          </div>
        </div>
        <router-link :to="{ name: 'quote' }" class="cta-button">
          Nueva {{ quoteTextSingular }}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12H19M19 12L12 5M19 12L12 19"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </router-link>
      </div>
    </template>
  </div>
</template>
<script>
export default {
  name: "myquotes",
  data() {
    return {
      loading: true,
      perPage: 9,
      currentPage: 1,
      items: [],
      activeFilter: "all",
    };
  },
  mounted() {
    let self = this;
    this.axios
      .get("quotes/")
      .then((result) => {
        self.loading = false;
        self.items = result.data
          .filter((quote) => quote.project && quote.quote_assets.length > 0)
          .map(function (quote) {
            let elem = {};
            elem["date"] = quote.FECHA_COTIZ;
            elem["asset"] =
              quote.quote_assets[0].asset.TIPO_BIEN +
              " " +
              quote.quote_assets[0].asset.NUM_BIEN;
            elem["project"] = quote.project.NOM_PROYECTO;
            elem["address"] =
              quote.project.CALLE +
              (quote.project.COMUNA.length > 0
                ? ", " + quote.project.COMUNA
                : "");
            elem["quote"] = quote.ID_COTIZACION;
            elem["validity"] = self.getQuoteValidity(quote);
            elem["reserve"] = {
              isReserve: self.canReserve(quote),
              isBusiness: quote.NEGOCIO,
              link:
                "https://reserva.mobysuite.com/" +
                self.$store.state.customer +
                "/proyecto/" +
                quote.project.NOM_PROYECTO.split(" ").join("-") +
                "?code=" +
                quote.CODIGO,
              projectId: quote.project.ID_PROYECTO,
            };
            return elem;
          });
      })
      .catch(() => {
        self.items = [];
        self.loading = false;
      });
  },
  watch: {
    activeFilter() {
      // Reset to first page when filter changes
      this.currentPage = 1;
    },
    currentPage() {
      // Scroll to top of content when page changes
      this.$nextTick(() => {
        if (this.$refs.mainContent) {
          this.$refs.mainContent.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      });
    },
  },
  computed: {
    customer() {
      return this.$store.getters.getCustomer;
    },
    user() {
      return this.$auth.user();
    },
    rows() {
      return this.items.length;
    },
    filteredRows() {
      return this.filteredItems.length;
    },
    country() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.country
        : 1;
    },
    quoteText() {
      return this.country == 0 ? "proformas" : "cotizaciones";
    },
    reservationText() {
      return this.country == 0 ? "Separada" : "Reservada";
    },
    quoteTextSingular() {
      return this.country == 0 ? "proforma" : "cotización";
    },
    showQuote() {
      return this.$store.getters.getCustomerData
        ? this.$store.getters.getCustomerData.show_quote
        : false;
    },
    filteredItems() {
      if (this.activeFilter === "all") {
        return this.items;
      } else if (this.activeFilter === "active") {
        return this.items.filter(
          (item) => item.reserve.isReserve && !item.reserve.isBusiness
        );
      } else if (this.activeFilter === "reserved") {
        return this.items.filter((item) => item.reserve.isBusiness);
      } else if (this.activeFilter === "expired") {
        return this.items.filter(
          (item) => !item.reserve.isReserve && !item.reserve.isBusiness
        );
      }
      return this.items;
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },
    paginationStart() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.filteredRows);
    },
    activeQuotesCount() {
      return this.items.filter(
        (item) => item.reserve.isReserve && !item.reserve.isBusiness
      ).length;
    },
    reservedQuotesCount() {
      return this.items.filter((item) => item.reserve.isBusiness).length;
    },
    expiredQuotesCount() {
      return this.items.filter(
        (item) => !item.reserve.isReserve && !item.reserve.isBusiness
      ).length;
    },
  },
  methods: {
    formatDate(value, format = "DD MMM YYYY") {
      if (!value) return "";
      try {
        if (this.$moment) {
          const m = this.$moment(value);
          if (m && typeof m.format === "function") return m.format(format);
        }
      } catch (e) {
        // ignore and fall back
      }

      try {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime()))
          return date.toLocaleDateString("es-CL");
      } catch (e) {
        // ignore
      }

      return String(value);
    },
    getQuoteValidity(quote) {
      return quote.quote_discounts.length > 0
        ? quote.project.DIAS_VALIDEZ_COTIZACION_CON_DESCUENTO
        : quote.project.DIAS_VALIDEZ_COTIZACION;
    },
    canReserve(quote) {
      let days =
        Math.abs(new Date() - new Date(quote.FECHA_COTIZ)) /
        (60 * 60 * 24 * 1000);
      let validity =
        quote.quote_discounts.length > 0
          ? quote.project.DIAS_VALIDEZ_COTIZACION_CON_DESCUENTO
          : quote.project.DIAS_VALIDEZ_COTIZACION;
      return validity - days >= 0 ? true : false;
    },
    getDaysRemaining(item) {
      const days =
        Math.abs(new Date() - new Date(item.date)) / (60 * 60 * 24 * 1000);
      const remaining = Math.max(0, Math.ceil(item.validity - days));
      return remaining;
    },
    getStatusClass(item) {
      if (item.reserve.isBusiness) return "status-reserved";
      if (item.reserve.isReserve) return "status-active";
      return "status-expired";
    },
    getCardClass(item) {
      if (item.reserve.isBusiness) return "card-reserved";
      if (item.reserve.isReserve) return "card-active";
      return "card-expired";
    },
    getStatusText(item) {
      if (item.reserve.isBusiness) return this.reservationText;
      if (item.reserve.isReserve) return "Vigente";
      return "Vencida";
    },
  },
};
</script>

<style scoped>
/* ===== Quotes Page Premium Styles ===== */
.quotes-page {
  /* Variables dinámicas basadas en el tema de la inmobiliaria */
  --theme-primary: var(--color-btn, #3b82f6);
  --theme-primary-hover: var(--color-btn-a, #2563eb);
  --theme-primary-text: var(--color-btn-text, #ffffff);
  --theme-accent: var(--color-a, #3b82f6);

  background: transparent;
  min-height: 100vh;
}

/* ===== SKELETON LOADING STYLES ===== */
.skeleton-container {
  animation: skeletonFadeIn 0.3s ease;
}

@keyframes skeletonFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.skeleton-header-section {
  margin-bottom: 24px;
}

.skeleton-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.skeleton-header-text {
  flex: 1;
  min-width: 280px;
}

.skeleton-title {
  width: 220px;
  height: 32px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 12px;
}

.skeleton-subtitle {
  width: 350px;
  max-width: 100%;
  height: 20px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
}

.skeleton-stats-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-stats-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.skeleton-stats-number {
  width: 40px;
  height: 28px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-stats-label {
  width: 120px;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.skeleton-filter-btn {
  width: 120px;
  height: 40px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 10px;
}

.skeleton-quotes-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.skeleton-quote-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.skeleton-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.skeleton-quote-id {
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-status-badge {
  width: 70px;
  height: 24px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 20px;
}

.skeleton-card-body {
  padding: 20px;
}

.skeleton-property-name {
  width: 70%;
  height: 22px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
  margin-bottom: 10px;
}

.skeleton-project-name {
  width: 50%;
  height: 18px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 16px;
}

.skeleton-details {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skeleton-detail-item {
  width: 80%;
  height: 16px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-card-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
}

.skeleton-action-btn {
  width: 130px;
  height: 36px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ===== HEADER SECTION ===== */
.quotes-header-section {
  padding: 0;
  margin-bottom: 24px;
  animation: headerSectionFadeIn 0.5s ease;
}

@keyframes headerSectionFadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quotes-header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;
}

.quotes-header-text {
  flex: 1;
  min-width: 280px;
}

.quotes-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
  animation: titleSlideIn 0.4s ease 0.1s backwards;
}

@keyframes titleSlideIn {
  from {
    opacity: 0;
    transform: translateX(-15px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.quotes-subtitle {
  font-size: 0.9375rem;
  color: #6b7280;
  margin: 0;
  max-width: 500px;
  line-height: 1.5;
  animation: subtitleFadeIn 0.4s ease 0.2s backwards;
}

@keyframes subtitleFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Stats Card */
.quotes-stats-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px 24px;
  animation: statsCardSlide 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s
    backwards;
}

@keyframes statsCardSlide {
  from {
    opacity: 0;
    transform: translateX(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

.stats-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  background: var(--theme-primary);
  border-radius: 12px;
  color: white;
  animation: iconPulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s backwards;
}

@keyframes iconPulse {
  from {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.stats-info {
  display: flex;
  flex-direction: column;
}

.stats-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  animation: numberCount 0.6s ease 0.6s backwards;
}

@keyframes numberCount {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-top: 4px;
  text-transform: capitalize;
}

/* ===== FILTER TABS ===== */
.quotes-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  animation: filtersFadeIn 0.4s ease 0.3s backwards;
}

@keyframes filtersFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.filter-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #374151;
}

.filter-btn.active {
  background: var(--theme-primary);
  border-color: var(--theme-primary);
  color: var(--theme-primary-text);
}

.filter-btn.active svg {
  color: var(--theme-primary-text);
}

.filter-btn svg {
  color: #9ca3af;
  transition: color 0.2s ease;
}

.filter-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 11px;
  font-size: 0.75rem;
  font-weight: 600;
}

.filter-btn.active .filter-count {
  background: rgba(255, 255, 255, 0.25);
}

/* ===== CONTENT CONTAINER ===== */
.quotes-content-container {
  background: #ffffff;
  border-radius: 16px;
  animation: containerFadeIn 0.4s ease;
}

@keyframes containerFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state.filter-empty {
  padding: 60px 24px;
}

.empty-icon {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 24px;
  color: #9ca3af;
  margin-bottom: 24px;
}

.empty-icon.small {
  width: 80px;
  height: 80px;
  border-radius: 16px;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 0.9375rem;
  color: #6b7280;
  max-width: 400px;
  margin-bottom: 24px;
}

.empty-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.empty-action:hover {
  background: var(--theme-primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  color: var(--theme-primary-text);
}

.reset-filter-btn {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-filter-btn:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

/* ===== QUOTES GRID ===== */
.quotes-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

/* ===== QUOTE CARD - Minimal Design ===== */
.quote-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  animation: cardFadeIn 0.4s ease backwards;
}

.quote-card:nth-child(1) {
  animation-delay: 0.05s;
}
.quote-card:nth-child(2) {
  animation-delay: 0.1s;
}
.quote-card:nth-child(3) {
  animation-delay: 0.15s;
}
.quote-card:nth-child(4) {
  animation-delay: 0.2s;
}
.quote-card:nth-child(5) {
  animation-delay: 0.25s;
}
.quote-card:nth-child(6) {
  animation-delay: 0.3s;
}
.quote-card:nth-child(7) {
  animation-delay: 0.35s;
}
.quote-card:nth-child(8) {
  animation-delay: 0.4s;
}
.quote-card:nth-child(9) {
  animation-delay: 0.45s;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.quote-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.quote-card.card-reserved {
  background: linear-gradient(to right, rgba(59, 130, 246, 0.02), #ffffff);
}

.quote-card.card-active:hover {
  border-color: #22c55e;
}

.quote-card.card-reserved:hover {
  border-color: var(--theme-primary);
}

/* Status Bar - Left accent */
.status-bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
}

.status-bar.status-active {
  background: #22c55e;
}

.status-bar.status-reserved {
  background: var(--theme-primary);
}

.status-bar.status-expired {
  background: #d1d5db;
}

/* Card Content */
.card-content {
  padding: 18px 18px 18px 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Top Row */
.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.quote-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.quote-id {
  font-weight: 600;
  color: #374151;
}

.quote-separator {
  color: #d1d5db;
}

.quote-date {
  font-weight: 400;
}

/* Status Badge */
.status-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-badge.status-active {
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
}

.status-badge.status-reserved {
  background: rgba(59, 130, 246, 0.1);
  background: var(--theme-primary-bg, rgba(59, 130, 246, 0.1));
  color: var(--theme-primary);
}

.status-badge.status-expired {
  background: #f3f4f6;
  color: #9ca3af;
}

/* Main Info */
.card-main {
  padding: 4px 0;
}

.property-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px;
  text-transform: capitalize;
  line-height: 1.3;
}

.project-title {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--theme-primary);
  margin: 0;
  opacity: 0.9;
}

/* Location */
.card-location {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 0.8125rem;
  color: #6b7280;
  line-height: 1.4;
}

.card-location svg {
  color: #9ca3af;
  flex-shrink: 0;
  margin-top: 1px;
}

/* Bottom Row */
.card-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  margin-top: 4px;
}

/* Validity Info */
.validity-info {
  display: flex;
  align-items: center;
}

.validity-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 6px;
}

.validity-tag.active {
  background: rgba(34, 197, 94, 0.08);
  color: #16a34a;
}

.validity-tag.active svg {
  color: #22c55e;
}

.validity-tag.reserved {
  background: rgba(59, 130, 246, 0.08);
  background: var(--theme-primary-bg-light, rgba(59, 130, 246, 0.08));
  color: var(--theme-primary);
}

.validity-tag.expired {
  background: #f9fafb;
  color: #9ca3af;
}

/* Card Actions */
.card-action {
  display: flex;
  align-items: center;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  font-size: 0.8125rem;
  font-weight: 600;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.action-btn:hover {
  opacity: 0.9;
  transform: translateX(2px);
  text-decoration: none;
  color: var(--theme-primary-text);
}

.action-contact {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #d97706;
  padding: 6px 12px;
  background: rgba(217, 119, 6, 0.08);
  border-radius: 6px;
}

.action-completed {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(59, 130, 246, 0.1);
  background: var(--theme-primary-bg, rgba(59, 130, 246, 0.1));
  border-radius: 50%;
  color: var(--theme-primary);
}

.action-expired {
  color: #d1d5db;
  font-size: 1rem;
}

/* ===== PAGINATION ===== */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.custom-pagination {
  margin: 0;
}

.custom-pagination >>> .page-item .page-link {
  border: none;
  background: transparent;
  color: #6b7280;
  padding: 8px 14px;
  margin: 0 2px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.custom-pagination >>> .page-item.active .page-link {
  background: var(--theme-primary);
  color: var(--theme-primary-text);
}

.custom-pagination >>> .page-item .page-link:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.custom-pagination >>> .page-item.active .page-link:hover {
  background: var(--theme-primary-hover);
  color: var(--theme-primary-text);
}

/* ===== INFO NOTE ===== */
.quotes-note {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  padding: 14px 18px;
  background: #f9fafb;
  border-radius: 10px;
  font-size: 0.8125rem;
  color: #6b7280;
}

.quotes-note svg {
  color: #9ca3af;
  flex-shrink: 0;
}

/* ===== CTA BANNER ===== */
.cta-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 28px;
  padding: 24px 28px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  animation: ctaFadeIn 0.4s ease 0.5s backwards;
}

@keyframes ctaFadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cta-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.cta-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-radius: 12px;
  color: #6b7280;
}

.cta-text h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 4px;
}

.cta-text p {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: var(--theme-primary);
  color: var(--theme-primary-text);
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
}

.cta-button:hover {
  background: var(--theme-primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  text-decoration: none;
  color: var(--theme-primary-text);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 1200px) {
  .quotes-grid,
  .skeleton-quotes-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .quotes-header-content {
    flex-direction: column;
    gap: 16px;
  }

  .quotes-stats-card {
    width: 100%;
  }

  .quotes-filters {
    gap: 8px;
  }

  .filter-btn {
    padding: 8px 14px;
    font-size: 0.8125rem;
  }

  .quotes-grid,
  .skeleton-quotes-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .cta-banner {
    flex-direction: column;
    text-align: center;
    gap: 20px;
    padding: 24px 20px;
  }

  .cta-content {
    flex-direction: column;
  }

  .card-content {
    padding: 16px 16px 16px 20px;
    gap: 10px;
  }

  .property-title {
    font-size: 0.9375rem;
  }

  .card-location {
    font-size: 0.75rem;
  }
}

@media (max-width: 576px) {
  .quotes-title {
    font-size: 1.5rem;
  }

  .quotes-stats-card {
    padding: 14px 18px;
  }

  .stats-icon {
    width: 44px;
    height: 44px;
  }

  .stats-icon svg {
    width: 22px;
    height: 22px;
  }

  .stats-number {
    font-size: 1.5rem;
  }

  .quotes-filters {
    overflow-x: auto;
    padding-bottom: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .filter-btn {
    white-space: nowrap;
    flex-shrink: 0;
  }

  .quotes-grid,
  .skeleton-quotes-grid {
    grid-template-columns: 1fr;
  }

  .quotes-content-container {
    border-radius: 12px;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .card-content {
    padding: 14px 14px 14px 18px;
    gap: 8px;
  }

  .quote-meta {
    font-size: 0.75rem;
  }

  .status-badge {
    font-size: 0.625rem;
    padding: 3px 8px;
  }

  .property-title {
    font-size: 0.9375rem;
  }

  .project-title {
    font-size: 0.8125rem;
  }

  .card-location {
    font-size: 0.75rem;
  }

  .card-bottom {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding-top: 10px;
  }

  .card-action {
    width: 100%;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .empty-state {
    padding: 48px 20px;
  }

  .empty-icon {
    width: 100px;
    height: 100px;
  }

  .cta-banner {
    padding: 20px 16px;
  }
}
</style>
